import { useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, newKey } from '../api/client';
import { formatGhs, parseCedis } from '../format';
import { Button, Field, Modal, useReport, useToast } from '../ui/kit';

const METHOD_LABEL: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card', BANK_TRANSFER: 'Bank transfer', ROOM: 'Guest’s room' };

/**
 * One dialog for every refund. A manager says why, and for mobile money or card gives the reference of the refund they
 * made on the provider's app or terminal (the software does not move that money; it records it). Cash comes out of
 * the manager's own till session. A fresh Idempotency-Key is kept until it succeeds, so a second tap cannot refund twice.
 */
export function RefundDialog({
  title,
  path,
  paid,
  partial,
  withCancel,
  onClose,
}: {
  title: string;
  path: string;
  /** how the money came in: [{ method, amount }] */
  paid: { method: string; amount: number }[];
  /** for a room payment: only what was overpaid can go back, and the manager may choose less */
  partial?: { max: number };
  /** pool and transport: offer to cancel the booking at the same time */
  withCancel?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const report = useReport();
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState(partial ? (partial.max / 100).toFixed(2) : '');
  const [momo, setMomo] = useState('');
  const [card, setCard] = useState('');
  const [single, setSingle] = useState('');
  const [cancel, setCancel] = useState(true);
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());

  const methods = [...new Set(paid.map((p) => p.method))];
  const total = paid.reduce((s, p) => s + p.amount, 0);
  const amt = partial ? parseCedis(amount) : total;
  const needMomo = !partial && methods.includes('MOMO');
  const needCard = !partial && methods.includes('CARD');
  const needSingle = !!partial && methods[0] !== 'CASH';
  const cash = !partial ? paid.filter((p) => p.method === 'CASH').reduce((s, p) => s + p.amount, 0) : methods[0] === 'CASH' ? (amt ?? 0) : 0;
  const valid = reason.trim().length >= 3 && amt !== null && amt > 0 && (!partial || amt <= partial.max) && (!needMomo || momo.trim().length >= 4) && (!needCard || card.trim().length >= 4) && (!needSingle || single.trim().length >= 4);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (partial) {
        body.amount = amt;
        if (needSingle) body.reference = single.trim();
      } else {
        body.references = { ...(needMomo ? { MOMO: momo.trim() } : {}), ...(needCard ? { CARD: card.trim() } : {}) };
        if (withCancel) body.cancel = cancel;
      }
      await api(path, { method: 'POST', body, idempotencyKey: key.current });
      key.current = newKey();
      toast.ok('Refund recorded');
      void qc.invalidateQueries();
      onClose();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <div className="total-row">
          <span>Money going back</span>
          <span className="amount num">{formatGhs(amt ?? 0)}</span>
        </div>
        <p className="small muted">
          Paid by {paid.map((p) => `${METHOD_LABEL[p.method] ?? p.method} ${formatGhs(p.amount)}`).join(' and ')}.
          {cash > 0 ? ' The cash comes out of your open till session.' : ''}
        </p>

        {partial ? (
          <Field id="rf-amt" label="Amount to refund (GH₵)" hint={`Up to ${formatGhs(partial.max)}, the amount overpaid. To refund for something not delivered, void that charge first.`} error={amt !== null && amt > partial.max ? 'More than was overpaid' : undefined}>
            <input id="rf-amt" className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
        ) : null}

        <Field id="rf-why" label="Why is this being refunded?" hint="Kept in the audit log with your name">
          <input id="rf-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>

        {needMomo ? (
          <Field id="rf-momo" label="Mobile money refund transaction ID" hint="Send the money back on the MoMo app first, then enter its ID here">
            <input id="rf-momo" className="input" value={momo} onChange={(e) => setMomo(e.target.value)} autoCapitalize="characters" />
          </Field>
        ) : null}
        {needCard ? (
          <Field id="rf-card" label="Card refund slip number" hint="Refund on the card terminal first. Never type a card number.">
            <input id="rf-card" className="input" value={card} onChange={(e) => setCard(e.target.value)} autoCapitalize="characters" />
          </Field>
        ) : null}
        {needSingle ? (
          <Field id="rf-ref" label={methods[0] === 'MOMO' ? 'Mobile money refund transaction ID' : methods[0] === 'CARD' ? 'Card refund slip number' : 'Transfer reference'} hint="From the refund you made outside this system">
            <input id="rf-ref" className="input" value={single} onChange={(e) => setSingle(e.target.value)} autoCapitalize="characters" />
          </Field>
        ) : null}

        {withCancel ? (
          <label className="row small">
            <input type="checkbox" checked={cancel} onChange={(e) => setCancel(e.target.checked)} /> Also cancel the booking
          </label>
        ) : null}

        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" disabled={!valid} loading={busy}>
            Refund {formatGhs(amt ?? 0)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
